import { makeStyles } from '@material-ui/core';
import L, { DivIcon } from 'leaflet';
import React, { useMemo } from 'react';
import { Marker } from 'react-leaflet';
import * as ReactDOMServer from 'react-dom/server';
import { Car } from './Car';
import { MOBILITY_VEHICLE_ANIMATION_DURATION } from '.';



const useStyle = makeStyles(() => {
    return {
        root: {
            background: 'none',
            border    : 'none',
            height    : 'fit-content',
            transition: `transform ${MOBILITY_VEHICLE_ANIMATION_DURATION}ms linear`,
        },

        carIcon: {
            transition: 'transform 200ms linear',
        },
    };
}, { name: 'VehicleMarker', index: 1 });



export interface VehicleMarkerProps {
    readonly latitude: number;
    readonly longitude: number;
    readonly heading?: number | undefined;
    readonly color?: string | undefined;

    // The size of the car icon. The default value is 1 for 1x. To double the icon size, use value 2. The value must be between 1 and 5 and will
    // automatically be set to 1 to 5 if it goes over the bounds.
    readonly size?: number | undefined;
}


/**
 * This is a custom Marker component that displays an image of a Car as marker icon.
 */
export const VehicleMarker: React.FC<VehicleMarkerProps> = (props: VehicleMarkerProps) => {

    const classes = useStyle();

    // Validate the icon's size prop and make sure it is between 1 and 5.
    const iconSize = useMemo(() => {
        if (props.size !== undefined) {
            return Math.max(1, Math.min(5, props.size));
        }
        return 1;
    }, [props.size]);


    const icon = useMemo((): DivIcon => {
        const rotation = props.heading ?? 0;
        return L.divIcon({
            className : classes.root,
            iconSize  : [26 * iconSize, 60 * iconSize],
            iconAnchor: [(26 * iconSize) / 2, (60 * iconSize) / 2],
            html      : ReactDOMServer.renderToString(<Car
                className={classes.carIcon}
                color={props.color}
                style={{
                    transform: `rotateZ(${rotation}deg)`,
                }}
            />),
        });
    }, [props.heading, props.color, classes.root, classes.carIcon, iconSize]);


    return (
        <Marker
            position={[props.latitude, props.longitude]}
            icon={icon}
        />
    );
};
